// src/main.ts
import './styles/theme.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';

import { render, type TemplateResult } from 'lit-html';
import { Router } from './modules/router.js';
import { Store } from './modules/store.js';
import { renderHome } from './views/home.js';
import { renderIntro, bindIntro } from './views/intro.js';
import { renderTest, bindTest } from './views/test.js';
import { score } from './modules/scoring.js';
import questionnaireARaw from './data/questionnaire-a.json';
import questionnaireBRaw from './data/questionnaire-b.json';
import { renderResult, bindResult } from './views/result.js';
import { renderAbout } from './views/about.js';
import { renderPrivacy } from './views/privacy.js';
import { renderError } from './views/error.js';
import { generateShareCard, downloadDataUrl } from './modules/share-card.js';
import { showToast } from './components/toast.js';
import { pickStrategies } from './modules/strategies.js';
import profilesRaw from './data/profiles.json';
import type { Questionnaire, ProfilesData } from './types/index.js';

const questionnaireA = questionnaireARaw as unknown as Questionnaire;
const questionnaireB = questionnaireBRaw as unknown as Questionnaire;
const profiles = profilesRaw as unknown as ProfilesData;

const store = new Store();
const root = document.getElementById('app');
if (!root) {
  throw new Error('#app element not found in DOM');
}
const renderView = (template: TemplateResult): void => {
  // bindXxx 通过 addEventListener 绑事件,lit-html DOM 复用会让旧 listener 残留;
  // 强制清空容器并丢弃 lit-html 缓存的 ChildPart 以避免事件累积。
  // TODO: 把 bindXxx 改为模板内联 @click,即可移除这两行,享受 lit-html 增量更新。
  root.innerHTML = '';
  delete (root as unknown as Record<string, unknown>)['_$litPart$'];
  render(template, root);
  window.scrollTo(0, 0);
};

const router = new Router({
  fallback: (path: string) => renderView(renderError(`未知页面: ${path}`)),
});

function lookupDimFor(qSet: Questionnaire) {
  return (qid: number, optId: string): string => {
    const q = qSet.questions.find(x => x.id === qid);
    if (!q) throw new Error(`question not found: ${qid}`);
    const opt = q.options.find(o => o.id === optId);
    if (!opt) throw new Error(`option not found: q=${qid} opt=${optId}`);
    return opt.dim;
  };
}

router
  .add('/',         () => renderView(renderHome({ store })))
  .add('/intro',    () => {
    renderView(renderIntro({ store }));
    bindIntro(root, store, router);
  })
  .add('/test',     () => {
    renderView(renderTest({ store }));
    if (store.state.status === 'in_progress') {
      bindTest(root, store, router, () => {
        const data = store.state.questionnaire === 'A' ? questionnaireA : questionnaireB;
        const result = score(store.state.answers, lookupDimFor(data));
        store.completeSession(result);
        router.navigate('/result');
      });
    }
  })
  .add('/result',   () => {
    renderView(renderResult({ store }));
    bindResult(root, store, {
      onShare: async () => {
        const last = store.history[0];
        if (!last) {
          showToast('暂无结果可分享', { type: 'warn' });
          return;
        }
        const profile = profiles[last.result.code] ?? null;
        try {
          const url = await generateShareCard({
            result: last.result,
            profile,
            strategies: pickStrategies(last.result.code, 3),
            orientation: 'portrait',
          });
          downloadDataUrl(url, `vark-${last.result.code}.png`);
        } catch (err) {
          console.error('share card generation failed', err);
          showToast('分享卡片生成失败', { type: 'error' });
        }
      },
    });
  })
  .add('/about',    () => renderView(renderAbout()))
  .add('/privacy',  () => renderView(renderPrivacy()))
  .start();
